import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fetchAuthedBlob, uploadFormData, type ApiResponse } from "@/api/client";
import { BookUser, Check, Loader2, Plane, ShieldCheck, ShieldAlert, Upload, X } from "lucide-react";

/**
 * Passports & TICKET CHECK panel (Block 5.2) on the order detail. Three jobs:
 *  1) the scan confirm queue — MRZ-extracted passport data is OFFERED as a diff and an
 *     operator confirms it into a participant (never a blind write; checksum-failed
 *     scans only show the photo + "type manually"),
 *  2) "Paste PNR" — raw Amadeus text → parsed legs + passenger names onto a flight
 *     booking's details (preview → apply via the existing booking PUT),
 *  3) the ticket check itself — names/DOB vs PNR with the digital "checked by + date".
 */

interface ScanRow {
  id: string;
  orderId: string;
  participantId: string | null;
  source: string;
  status: string;
  mrz: string | null;
  extracted: {
    lastName: string;
    firstName: string;
    passportNumber: string;
    nationality: string;
    birthDate: string;
    expiryDate: string;
    sex: string;
    issuingCountry: string;
  } | null;
  checksumsValid: boolean | null;
  createdAt: string;
}

interface PaxLite {
  id?: string;
  firstName: string | null;
  lastName: string | null;
}

interface TicketCheckRowApi {
  participantId: string;
  name: string;
  nameMatch: "match" | "fuzzy" | "missing" | "no_pnr";
  closestPnrName: string | null;
  hasPassport: boolean;
  expiryOk: boolean | null;
  passportExpiry: string | null;
  checked: boolean;
}

interface TicketCheckApi {
  rows: TicketCheckRowApi[];
  unmatchedPnrNames: string[];
  summary: { pax: number; match: number; fuzzy: number; missing: number; checked: number };
}

interface FlightBookingLite {
  id: string;
  kind: string;
  snapshotName: string;
}

interface PnrPreview {
  legs: Array<{
    flightNumber: string;
    from: string;
    to: string;
    departAt: string;
    arriveAt: string | null;
    status: string;
    paxCount: number;
  }>;
  paxNames: Array<{ lastName: string; firstName: string }>;
  locator: string | null;
  unparsed: string[];
}

const MATCH_CHIP: Record<string, { cls: string; label: string }> = {
  match: { cls: "bg-emerald-100 text-emerald-700", label: "PNR ✓" },
  fuzzy: { cls: "bg-amber-100 text-amber-700", label: "check spelling" },
  missing: { cls: "bg-red-100 text-red-700", label: "not on PNR" },
  no_pnr: { cls: "bg-gray-100 text-gray-500", label: "no PNR yet" },
};

/** Authed photo preview — the photo endpoint is role-gated PII, so no plain <img src>. */
function ScanPhoto({ scanId }: { scanId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoke: string | null = null;
    fetchAuthedBlob(`/api/admin/passports/scans/${scanId}/photo`)
      .then((blob) => {
        revoke = URL.createObjectURL(blob);
        setUrl(revoke);
      })
      .catch(() => setFailed(true));
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [scanId]);
  if (failed)
    return (
      <div className="flex h-28 w-40 items-center justify-center rounded bg-gray-100 text-xs text-gray-400 dark:bg-gray-700">
        photo restricted
      </div>
    );
  if (!url)
    return (
      <div className="flex h-28 w-40 items-center justify-center rounded bg-gray-100 dark:bg-gray-700">
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      </div>
    );
  return <img src={url} alt="Passport scan" className="h-28 w-40 rounded object-cover" />;
}

export default function PassportPanel({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  // Same key shape as OrderParticipantsCard (reveal=false) — shares its cache.
  const { data: paxData } = useQuery({
    queryKey: ["order-participants", orderId, false],
    queryFn: () => api.get<{ data: PaxLite[] }>(`/api/admin/orders/${orderId}/participants`),
  });
  const participants = paxData?.data ?? [];

  const { data: scansData } = useQuery({
    queryKey: ["passport-scans", orderId],
    queryFn: () => api.get<ApiResponse<ScanRow[]>>(`/api/admin/passports/scans?orderId=${orderId}`),
  });
  const scans = scansData?.data ?? [];

  const { data: checkData } = useQuery({
    queryKey: ["ticket-check", orderId],
    queryFn: () =>
      api.get<ApiResponse<TicketCheckApi>>(`/api/admin/passports/ticket-check?orderId=${orderId}`),
  });
  const check = checkData?.data;

  const { data: bookingsData } = useQuery({
    queryKey: ["service-bookings", orderId],
    queryFn: () =>
      api.get<{ data: FlightBookingLite[] }>(
        `/api/admin/service-bookings?orderId=${orderId}&limit=50`,
      ),
  });
  const flightBookings = (bookingsData?.data ?? []).filter((b) => b.kind === "flight");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["passport-scans", orderId] });
    qc.invalidateQueries({ queryKey: ["ticket-check", orderId] });
    qc.invalidateQueries({ queryKey: ["order-participants", orderId] });
  };

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("orderId", orderId);
      return uploadFormData<ApiResponse<ScanRow>>("/api/admin/passports/scans", fd);
    },
    onSuccess: invalidate,
  });

  const markMutation = useMutation({
    mutationFn: (args: { participantId: string; checked: boolean }) =>
      api.post(`/api/admin/passports/participants/${args.participantId}/mark-checked`, {
        checked: args.checked,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ticket-check", orderId] }),
  });

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <BookUser className="h-5 w-5" /> Passports & ticket check
          {scans.length > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              {scans.length} scan{scans.length > 1 ? "s" : ""} to confirm
            </span>
          )}
        </h2>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadMutation.mutate(f);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="btn-secondary btn-sm"
            title="Upload a passport photo — MRZ is read and offered as a confirmable diff"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload passport photo
          </button>
        </div>
      </div>

      {/* Pending scans — the confirm queue. */}
      {scans.map((scan) => (
        <ScanCard key={scan.id} scan={scan} participants={participants} onDone={invalidate} />
      ))}

      {/* Ticket check table */}
      {check && check.rows.length > 0 && (
        <div>
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
            Ticket check
            <span className="text-xs font-normal text-gray-400">
              {check.summary.match}/{check.summary.pax} matched · {check.summary.checked} checked
            </span>
          </div>
          <div className="space-y-1">
            {check.rows.map((row) => (
              <div
                key={row.participantId}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm dark:border-gray-700"
              >
                <span className="min-w-[10rem] font-medium text-gray-900 dark:text-white">
                  {row.name}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${MATCH_CHIP[row.nameMatch].cls}`}
                >
                  {MATCH_CHIP[row.nameMatch].label}
                </span>
                {row.closestPnrName && (
                  <span className="text-xs text-gray-400">ticket: {row.closestPnrName}</span>
                )}
                {!row.hasPassport ? (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                    no passport
                  </span>
                ) : row.expiryOk === false ? (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                    expires {row.passportExpiry} — too soon
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() =>
                    markMutation.mutate({ participantId: row.participantId, checked: !row.checked })
                  }
                  disabled={markMutation.isPending}
                  className={`ml-auto flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                    row.checked
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-gray-100 text-gray-600 hover:bg-emerald-50 dark:bg-gray-700 dark:text-gray-300"
                  }`}
                  title={
                    row.checked
                      ? "Checked — click to clear"
                      : "Mark name/DOB verified against the ticket (checked by + date)"
                  }
                >
                  {row.checked ? (
                    <ShieldCheck className="h-3.5 w-3.5" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                  {row.checked ? "checked" : "mark checked"}
                </button>
              </div>
            ))}
          </div>
          {check.unmatchedPnrNames.length > 0 && (
            <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
              <ShieldAlert className="h-3.5 w-3.5" />
              On the PNR but not among participants: {check.unmatchedPnrNames.join(", ")}
            </p>
          )}
        </div>
      )}

      <PnrPasteBox orderId={orderId} flightBookings={flightBookings} onApplied={invalidate} />
    </div>
  );
}

// ── One pending scan: photo + extracted diff + confirm/reject ─────────────────────

function ScanCard({
  scan,
  participants,
  onDone,
}: {
  scan: ScanRow;
  participants: PaxLite[];
  onDone: () => void;
}) {
  const [participantId, setParticipantId] = useState(scan.participantId ?? "");

  const confirmMutation = useMutation({
    mutationFn: () => api.post(`/api/admin/passports/scans/${scan.id}/confirm`, { participantId }),
    onSuccess: onDone,
  });
  const rejectMutation = useMutation({
    mutationFn: () => api.post(`/api/admin/passports/scans/${scan.id}/reject`, {}),
    onSuccess: onDone,
  });

  const e = scan.extracted;
  const usable = scan.checksumsValid === true && e;

  return (
    <div className="flex flex-wrap gap-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900/50 dark:bg-amber-900/10">
      <ScanPhoto scanId={scan.id} />
      <div className="min-w-[16rem] flex-1">
        <p className="mb-1 text-xs text-gray-500">
          via {scan.source} · {new Date(scan.createdAt).toLocaleString("en-GB")}
        </p>
        {usable ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
            <span className="text-gray-500">Name</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {e.firstName} {e.lastName}
            </span>
            <span className="text-gray-500">Passport</span>
            <span className="font-mono font-medium text-gray-900 dark:text-white">
              {e.passportNumber}
            </span>
            <span className="text-gray-500">Born / expires</span>
            <span className="text-gray-900 dark:text-white">
              {e.birthDate} → {e.expiryDate}
            </span>
            <span className="text-gray-500">Nationality</span>
            <span className="text-gray-900 dark:text-white">{e.nationality}</span>
          </div>
        ) : (
          <p className="flex items-center gap-1.5 text-sm text-amber-700">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            MRZ unreadable or checksums failed — read the photo and type the data into the
            participant manually, then reject this scan.
          </p>
        )}
      </div>
      <div className="flex flex-col items-stretch justify-center gap-2">
        {usable && (
          <>
            <select
              value={participantId}
              onChange={(ev) => setParticipantId(ev.target.value)}
              className="input py-1 text-sm"
            >
              <option value="">Assign to traveller…</option>
              {participants
                .filter((p) => p.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {[p.firstName, p.lastName].filter(Boolean).join(" ") || p.id}
                  </option>
                ))}
            </select>
            <button
              type="button"
              onClick={() => confirmMutation.mutate()}
              disabled={!participantId || confirmMutation.isPending}
              className="btn-primary btn-sm"
            >
              {confirmMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Confirm into traveller
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => rejectMutation.mutate()}
          disabled={rejectMutation.isPending}
          className="btn-secondary btn-sm"
        >
          <X className="h-4 w-4" />
          Reject
        </button>
      </div>
    </div>
  );
}

// ── PNR paste box — preview → apply onto a flight booking's details ────────────────

function PnrPasteBox({
  orderId,
  flightBookings,
  onApplied,
}: {
  orderId: string;
  flightBookings: FlightBookingLite[];
  onApplied: () => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [bookingId, setBookingId] = useState("");
  const [preview, setPreview] = useState<PnrPreview | null>(null);

  const parseMutation = useMutation({
    mutationFn: () => api.post<ApiResponse<PnrPreview>>("/api/admin/passports/pnr/parse", { text }),
    onSuccess: (r) => setPreview(r.data),
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      // Merge into the booking's existing details — a blind PUT would wipe other keys
      // (room lists, policy numbers) sharing the JSON column.
      const current = await api.get<ApiResponse<{ details: Record<string, unknown> | null }>>(
        `/api/admin/service-bookings/${bookingId}`,
      );
      return api.put(`/api/admin/service-bookings/${bookingId}`, {
        details: {
          ...(current.data?.details ?? {}),
          legs: preview!.legs,
          paxNames: preview!.paxNames,
          pnrLocator: preview!.locator,
          pnrRaw: text,
        },
      });
    },
    onSuccess: () => {
      setOpen(false);
      setText("");
      setPreview(null);
      qc.invalidateQueries({ queryKey: ["service-bookings", orderId] });
      onApplied();
    },
  });

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="btn-ghost btn-sm">
        <Plane className="h-4 w-4" />
        Paste PNR (Amadeus)
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex items-center gap-2">
        <Plane className="h-4 w-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
          Paste the raw Amadeus display — segments & names are parsed for you
        </span>
        <button type="button" onClick={() => setOpen(false)} className="btn-ghost btn-sm ml-auto">
          <X className="h-4 w-4" />
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        className="input font-mono text-xs"
        placeholder={"RP/PRGCZ2100/...\n1.NOVAK/JAN MR\n2 VN 521 Y 20DEC 7 SGNHAN HK2 0800 1010"}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => parseMutation.mutate()}
          disabled={!text.trim() || parseMutation.isPending}
          className="btn-secondary btn-sm"
        >
          {parseMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Parse
        </button>
        {preview && (
          <>
            <select
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              className="input max-w-xs py-1 text-sm"
            >
              <option value="">Attach to flight booking…</option>
              {flightBookings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.snapshotName}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => applyMutation.mutate()}
              disabled={!bookingId || applyMutation.isPending || preview.legs.length === 0}
              className="btn-primary btn-sm"
            >
              {applyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Apply
            </button>
          </>
        )}
      </div>
      {preview && (
        <div className="text-xs">
          {preview.legs.map((l, i) => (
            <p key={i} className="text-gray-700 dark:text-gray-200">
              ✈ {l.flightNumber} {l.from}→{l.to} {l.departAt}
              {l.arriveAt ? ` → ${l.arriveAt}` : ""} ({l.status}
              {l.paxCount})
            </p>
          ))}
          {preview.paxNames.length > 0 && (
            <p className="text-gray-500">
              Pax: {preview.paxNames.map((n) => `${n.lastName}/${n.firstName}`).join(", ")}
            </p>
          )}
          {preview.locator && <p className="text-gray-500">Locator: {preview.locator}</p>}
          {preview.unparsed.length > 0 && (
            <p className="text-gray-400">Ignored lines: {preview.unparsed.length}</p>
          )}
          {preview.legs.length === 0 && (
            <p className="text-amber-600">No flight segments recognised — check the paste.</p>
          )}
        </div>
      )}
    </div>
  );
}
